import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Every new page starts at the top, also after the back and forward buttons: the browser's own scroll
// restoration is off, as it would put back an old position on a page that is still loading.
if (typeof window !== 'undefined' && window.history && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const reducedMotion = () => typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A jump before the new page is painted, not a smooth scroll after it: the new page first shows its
// short loading state, and a smooth scroll still running when its content comes in stops part way,
// often at the bottom of the page. What softens the change instead is the new page fading in (the
// site's <main>, App's ContentWrapper), not on the first load and not under prefers-reduced-motion.
function ScrollToTop() {
  const { pathname } = useLocation();
  // The path of the page shown before; it starts as the first page's own, so that one does not fade.
  // Compared rather than a "first time" flag, which React's StrictMode (a second run of each effect on
  // mount) would clear before the second run.
  const previous = useRef(pathname);
  // The fade still running when the next page comes (quick clicks): ended before the new one starts.
  const fade = useRef(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (previous.current === pathname) return;
    previous.current = pathname;
    const main = document.querySelector('main');
    if (main && typeof main.animate === 'function' && !reducedMotion()) {
      if (fade.current) fade.current.cancel();
      fade.current = main.animate(
        [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
        { duration: 320, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
      );
    }
  }, [pathname]);

  return null;
}

export default ScrollToTop;
